export type RgbaColor = [number, number, number, number];

/**
 * Convert a CSS hex color string ("#RRGGBB" or "#RGB") and optional opacity
 * into a deck.gl RGBA tuple [R, G, B, A] with A in 0-255 range.
 */
// CSS named colors used in polygon/text definitions across the codebase.
const CSS_NAMED_COLORS: Record<string, RgbaColor> = {
  black:   [0,   0,   0,   255],
  white:   [255, 255, 255, 255],
  red:     [255, 0,   0,   255],
  green:   [0,   128, 0,   255],
  blue:    [0,   0,   255, 255],
  cyan:    [0,   255, 255, 255],
  yellow:  [255, 255, 0,   255],
  gray:    [128, 128, 128, 255],
  grey:    [128, 128, 128, 255],
};

export function hexToRgba(hex: string | undefined, opacity = 1): RgbaColor {
  if (!hex) return [0, 0, 0, 0];

  // Handle CSS named colors (e.g. "black", "cyan") — polygon definitions use these.
  const named = CSS_NAMED_COLORS[hex.toLowerCase().trim()];
  if (named) {
    const a = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
    return [named[0], named[1], named[2], a];
  }

  const h = hex.replace('#', '');
  let r: number, g: number, b: number;
  if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
  } else if (h.length >= 6) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else {
    return [0, 0, 0, 0];
  }
  const a = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
  return [
    isNaN(r) ? 0 : r,
    isNaN(g) ? 0 : g,
    isNaN(b) ? 0 : b,
    a,
  ];
}

export function parseStrokeWidth(sw: string | undefined, fallback = 1): number {
  if (!sw) return fallback;
  const n = parseFloat(sw);
  return isNaN(n) ? fallback : Math.max(0, n);
}

export function parseFontSize(fs: string | undefined, fallback = 12): number {
  if (!fs) return fallback;
  const n = parseFloat(fs);
  return isNaN(n) ? fallback : Math.max(1, n);
}
